import React, { useContext, useEffect, useState, useMemo, memo } from "react";
import { useLocation } from "react-router-dom";
import Categoryicon from "../../../assets/images/icons/category_icon.svg";
import SubCategoryCardShimmer from "../shimmers/SubCategoryCardShimmer";
import { Language } from "../../../App";

const CategoryCard = ({ category, active, onClick }) => {
  const { lang } = useContext(Language);
  return (
    <div
      onClick={onClick}
      className={`cr-cat-chip${active ? " active" : ""}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick && onClick();
        }
      }}
    >
      <div className="cr-cat-icon-box">
        <img src={Categoryicon} width={18} height={18} alt="" />
      </div>
      <div>
        <p className="cr-cat-name">{category.name}</p>
        <p className="cr-cat-count">
          {category.courses_count} {lang?.courses || "Courses"}
        </p>
      </div>
    </div>
  );
};

const CategoryCardMemo = memo(({ category, active, onClick }) => (
  <CategoryCard category={category} active={active} onClick={onClick} />
));

const SubcategoryCards = ({
  categories = [],
  onCategoryDataFetched,
  defaultCoursesData,
  defaultHeading,
  loading,
  selectedCatId,
}) => {
  const location = useLocation();
  const { lang } = useContext(Language);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(loading);

  useEffect(() => {
    setActiveCategoryId(selectedCatId);
  }, [selectedCatId]);

  const shouldHideHeader = useMemo(() => {
    const hiddenRoutes = [
      "/courses/all/trending",
      "/courses/all/following-doctor",
      "/courses/all/popular-courses",
      "/courses/all/purchased-courses",
    ];
    return hiddenRoutes.some((path) => location.pathname.startsWith(path));
  }, [location.pathname]);

  const [firstPart, secondPart] = useMemo(() => {
    const middleIndex = Math.ceil(categories.length / 2);
    return [categories.slice(0, middleIndex), categories.slice(middleIndex)];
  }, [categories]);

  const handleCategoryClick = useMemo(
    () => (categoryId, name) => {
      if (activeCategoryId === categoryId) {
        setActiveCategoryId(null);
        onCategoryDataFetched(defaultCoursesData, defaultHeading, false, null);
        return;
      }
      setActiveCategoryId(categoryId);
      onCategoryDataFetched(null, name, isLoadingCategories, categoryId);
    },
    [activeCategoryId, defaultCoursesData, defaultHeading, isLoadingCategories, onCategoryDataFetched]
  );

  const categoryCards1 = useMemo(
    () =>
      firstPart.map((category) => (
        <CategoryCardMemo
          key={category.id}
          category={category}
          active={activeCategoryId === category.id}
          onClick={() => handleCategoryClick(category.id, category.name)}
        />
      )),
    [firstPart, activeCategoryId, handleCategoryClick]
  );

  const categoryCards2 = useMemo(
    () =>
      secondPart.map((category) => (
        <CategoryCardMemo
          key={category.id}
          category={category}
          active={activeCategoryId === category.id}
          onClick={() => handleCategoryClick(category.id, category.name)}
        />
      )),
    [secondPart, activeCategoryId, handleCategoryClick]
  );

  return (
    <div className="cr-section">
      {!shouldHideHeader && (
        <div className="cr-section-header">
          <div className="cr-section-title-wrap">
            <h3 className="cr-section-title">
              {lang.explore_course_categories || "Explore Course Categories"}
            </h3>
            <p className="cr-section-subtitle">
              Browse accredited masterclasses across clinical specializations
            </p>
          </div>
        </div>
      )}

      <div className="w-100 overflow-x-auto scroll-hide" style={{ WebkitOverflowScrolling: "touch" }}>
        {loading ? (
          <SubCategoryCardShimmer />
        ) : (
          <div className="d-flex flex-column gap-2 py-1">
            <div className="d-flex gap-2">{categoryCards1}</div>
            {secondPart.length > 0 && <div className="d-flex gap-2">{categoryCards2}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(SubcategoryCards);

